import type {
    Bi,
    Gender,
    Lakara,
    NounTable,
    Purusha,
    Vacana
} from '@/sanskrit/lib/sanskrit/types'
import type { NominalAnalysis, VerbAnalysis } from '@/sanskrit/lib/sanskrit/analyzer'

import { getDhatu } from '@/sanskrit/lib/sanskrit/dhatus'
import { declineStem, getNounParadigm } from '@/sanskrit/lib/sanskrit/nouns'
import { getVocab } from '@/sanskrit/lib/sanskrit/vocabulary'

const WHO: Record<Purusha, Record<Vacana, Bi>> = {
    prathama: {
        eka: { hi: 'वह (एक)', en: 'he / she / it' },
        dvi: { hi: 'वे दोनों', en: 'the two of them' },
        bahu: { hi: 'वे सब', en: 'they (3 or more)' }
    },
    madhyama: {
        eka: { hi: 'तुम', en: 'you (one)' },
        dvi: { hi: 'तुम दोनों', en: 'you two' },
        bahu: { hi: 'तुम सब', en: 'you all' }
    },
    uttama: {
        eka: { hi: 'मैं', en: 'I' },
        dvi: { hi: 'हम दोनों', en: 'we two' },
        bahu: { hi: 'हम सब', en: 'we (3 or more)' }
    }
}

export const whoOf = (p: Purusha, v: Vacana): Bi => WHO[p][v]

const WHEN: Record<Lakara, Bi> = {
    lat: { hi: 'अभी / सामान्यतः', en: 'now, or habitually' },
    lrt: { hi: 'भविष्य में', en: 'in the future' },
    lan: { hi: 'भूतकाल में (आज से पहले)', en: 'in the past (before today)' },
    lot: { hi: 'आज्ञा या प्रार्थना', en: 'as a command or request' },
    vidhilin: { hi: 'चाहिए / सम्भावना', en: 'should / might' },
    lit: { hi: 'बहुत पहले (परोक्ष)', en: 'long ago (unwitnessed)' },
    lut: { hi: 'आज के बाद, निश्चित', en: 'definitely, after today' },
    lun: { hi: 'भूतकाल में', en: 'in the past' },
    ashirlin: { hi: 'आशीर्वाद के रूप में', en: 'as a blessing or wish' },
    lrn: { hi: 'यदि... तो (जो नहीं हुआ)', en: 'would have (did not happen)' },
    let: { hi: 'वैदिक', en: 'Vedic' }
}

export const whenOf = (l: Lakara): Bi => WHEN[l]

const bare = (meaning: string) => meaning.replace(/^to\s+/i, '').split(',')[0].trim()

/** A short English gloss for a verb form: "I read", "they will go". Hindi uses a who/when breakdown instead. */
export const verbGlossEn = (a: Pick<VerbAnalysis, 'dhatuId' | 'lakara' | 'purusha' | 'vacana'>): string => {
    const d = getDhatu(a.dhatuId)
    if (!d) return ''
    const verb = bare(d.meaning.en)
    const who = WHO[a.purusha][a.vacana].en.replace(/ \(.*\)$/, '').split(' / ')[0]
    const third = a.purusha === 'prathama' && a.vacana === 'eka'
    const isBe = verb === 'be'

    switch (a.lakara) {
        case 'lat':
            if (isBe) return `${who} ${a.purusha === 'uttama' && a.vacana === 'eka' ? 'am' : third ? 'is' : 'are'}`
            if (!third) return `${who} ${verb}`
            if (/(o|s|sh|ch|x|z)$/.test(verb)) return `${who} ${verb}es`
            if (/[^aeiou]y$/.test(verb)) return `${who} ${verb.slice(0, -1)}ies`
            return `${who} ${verb}s`
        case 'lrt':
        case 'lut':
            return `${who} will ${verb}`
        case 'lan':
        case 'lun':
        case 'lit':
            return `${who} ${verb} (past)`
        case 'lot':
            return a.purusha === 'madhyama' ? `${verb}! (${who})` : `let ${who === 'I' ? 'me' : who} ${verb}`
        case 'vidhilin':
            return `${who} should ${verb}`
        case 'ashirlin':
            return `may ${who} ${verb}`
        case 'lrn':
            return `${who} would have ${verb}`
        default:
            return verb
    }
}

/** Full declension table for any nominal analysis, when the platform carries one. */
export const nounTableFor = (a: NominalAnalysis): NounTable | null => {
    if (a.paradigmId) return getNounParadigm(a.paradigmId)?.table ?? null

    if (a.vocabId) {
        const v = getVocab(a.vocabId)
        if (!v) return null

        if (a.kind === 'adjective') {
            if (a.gender === 'm') return declineStem(v.stem ?? a.stem, v.adjMascCls ?? 'a-m')
            if (a.gender === 'n') return declineStem(v.stem ?? a.stem, 'a-n')
            return declineStem(a.stem, a.stem === v.fem?.stem ? v.fem.cls : a.stem.endsWith('ी') ? 'ii-f' : 'aa-f')
        }

        if (v.paradigmId) return getNounParadigm(v.paradigmId)?.table ?? null
        if (v.cls && v.stem) return declineStem(v.stem, v.cls)
    }

    if (a.cls) return declineStem(a.stem, a.cls)

    return null
}

export const genderWord: Record<Gender, Bi> = {
    m: { hi: 'पुंलिङ्ग', en: 'masculine' },
    f: { hi: 'स्त्रीलिङ्ग', en: 'feminine' },
    n: { hi: 'नपुंसकलिङ्ग', en: 'neuter' }
}
